import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Autocomplete,
  TextField
} from "@mui/material";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import transactionAPI from "../../Network/Transaction";
import unitAPI from "../../Network/Unit";
import Title from "./Title";
import Grid from "../Grid/Grid";

const cookies = new Cookies();

export default function ReportUnitTransactionSummary() {
  const [t] = useTranslation("common");
  const navigate = useNavigate();
  const theme = useSelector((state) => state.themeData);
  const maintheme = theme?.maintheme;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    unit_id: null,
    unit: null,
    creation_date_from: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
    creation_date_to: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')
  });

  const [reload, setReload] = useState(false);

  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const timerUnits = useRef(null);

   const headerObject = {
    username: cookies.get("user_name") ? cookies.get("user_name") : null,
    user_id: cookies.get("user_id") ? cookies.get("user_id") : null,
    account_name: cookies.get("account_name") ? cookies.get("account_name") : null,
    account_id: cookies.get("account_id") ? cookies.get("account_id") : null,
    authHash: cookies.get("authHash") ? cookies.get("authHash") : null,
    sid: cookies.get("sid") ? cookies.get("sid") : null,
    baseurl: cookies.get("baseUrl") ? cookies.get("baseUrl") : null,
    id: cookies.get("id") ? cookies.get("id") : null,
  }

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await transactionAPI.getUnitTransactionsSummary({
        data: {
          ...filters,
          creation_date_from: filters.creation_date_from,
          creation_date_to: filters.creation_date_to
        },
        params: headerObject,
        
      });

      if (response.status && response?.data?.status) {
        const data = response?.data?.data || [];
        setRows(data.map((row, index) => ({ ...row, id: index })));
        setRowsTotal(data.length);
        setPageCount(Math.ceil(data.length / pageSize));
      } else {
        toast.error(response?.data?.message || t("NETWORK_MESSAGES.unknownError"));
      }
    } catch (error) {
      toast.error(error.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [reload]);

  const handleSearchTermUnitChange = (newValue) => {
    if (timerUnits.current) clearTimeout(timerUnits.current);
    timerUnits.current = setTimeout(async () => {
      if (newValue?.length >= 3) {
        setLoadingUnits(true);
        const result = await unitAPI.getUnitList({
          params: { ...headerObject, name: newValue, page_size: 20, page: 1 }
        });
        setLoadingUnits(false);
        if (result.status && result?.data?.data?.data) {
          setUnits(result?.data?.data?.data);
        }
      } else {
        setUnits([]);
      }
    }, 1000);
  };

  const handleRedirect = (row, audited_status = null) => {
    // 1 is usually External (TransactionsCustom), others might be Internal
    const path = row.lifecycle_type === 1 ? "/system/transactions_custom" : "/system/transactions_internal";
    
    // Pass filters via state
    navigate(path, {
      state: {
        unit_id: row.unit_id,
        unit_name: row.unit_name,
        creation_date_from: filters.creation_date_from,
        creation_date_to: filters.creation_date_to,
        audited_status: audited_status
      }
    });
  };

  const clearFilters = () => {
    setFilters({
      unit_id: null,
      unit: null,
      creation_date_from: moment().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      creation_date_to: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')
    });
  };

  const columns = [
    { 
      field: 'unit_name', 
      headerName: t('TRANSACTIONS.unit_name'), 
      flex: 1.5, 
      headerAlign: 'center', 
      align: 'center',
      renderCell: (params) => params.value || t('GLOBAL.undefine')
    },
    { 
      field: 'lifecycle_type', 
      headerName: t('TRANSACTIONS.transaction_type'), 
      flex: 1, 
      headerAlign: 'center', 
      align: 'center', 
      renderCell: (params) => {
        if (params.value === 1) return "جهد خارجي";
        if (params.value === 2) return "جهد داخلي";
        return "غير معروف"; // Show the number if unknown
      }
    },
    {
      field: 'total_transactions', headerName: t('TRANSACTIONS.total_transactions'), flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => (
        <Typography onClick={() => handleRedirect(params.row, 0)} sx={{ color: 'blue', cursor: 'pointer', fontWeight: 'bold' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'total_audited', headerName: t('TRANSACTIONS.total_audited'), flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => (
        <Typography onClick={() => handleRedirect(params.row, 2)} sx={{ color: 'green', cursor: 'pointer', fontWeight: 'bold' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'total_not_audited', headerName: t('TRANSACTIONS.total_not_audited'), flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => (
        <Typography onClick={() => handleRedirect(params.row, 1)} sx={{ color: 'red', cursor: 'pointer', fontWeight: 'bold' }}>
          {params.value}
        </Typography>
      )
    },
    { field: 'last_weight', headerName: t('TRANSACTIONS.last_weight'), flex: 1, headerAlign: 'center', align: 'center', renderCell: (params) => params.value ? params.value.toLocaleString() : '-' },
    { field: 'last_transaction_type', headerName: t('TRANSACTIONS.last_transaction_type'), flex: 1.2, headerAlign: 'center', align: 'center' }
  ];

  const handlePageChange = (newPage) => {
    setPageNumber(newPage + 1);
  };

  const searchFunction = () => {
    setReload(!reload);
    setPageNumber(1);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Title title="ملخص حركات المركبات" />
      
      <Grid
        rows={rows}
        columns={columns}
        pageSize={pageSize}
        setPageSize={setPageSize}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        loading={loading}
        rowsTotal={rowsTotal}
        pageCount={pageCount}
        handlePageChange={handlePageChange}
        paginationMode="client"
        fixHeight={'calc(100vh - 300px)'}
        searchFunction={searchFunction}
        clearFunction={clearFilters}
        CustomFilterBox={
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, p: 2 }}>
            <Autocomplete
              options={units}
              getOptionLabel={(option) => option.name || ""}
              loading={loadingUnits}
              onInputChange={(event, newInputValue) => handleSearchTermUnitChange(newInputValue)}
              onChange={(event, newValue) => setFilters({ ...filters, unit_id: newValue?.id, unit: newValue })}
              value={filters.unit}
              renderInput={(params) => <TextField {...params} label={t("UNITS.name")} size="small" />}
            />
            <TextField
              label={t("GLOBAL.from")}
              type="datetime-local"
              value={moment(filters.creation_date_from).format('YYYY-MM-DDTHH:mm')}
              onChange={(e) => setFilters({ ...filters, creation_date_from: moment(e.target.value).format('YYYY-MM-DD HH:mm:ss') })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label={t("GLOBAL.to")}
              type="datetime-local"
              value={moment(filters.creation_date_to).format('YYYY-MM-DDTHH:mm')}
              onChange={(e) => setFilters({ ...filters, creation_date_to: moment(e.target.value).format('YYYY-MM-DD HH:mm:ss') })}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Box>
        }
      />
    </Box>
  );
}
